import { useState, useEffect, useRef } from "react";
import API from "../api";
import CreateOrderModal from "../components/CreateOrderModal";
import InventorySidebar from "../components/InventorySidebar";

function Orders() {

  const [orders, setOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showInventory, setShowInventory] = useState(true);

  const [customerFilter, setCustomerFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [inventoryRefreshKey, setInventoryRefreshKey] = useState(0);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const activeFilterCount = [customerFilter, dateFilter, statusFilter].filter(Boolean).length;

  const refreshInventory = () => {
    setInventoryRefreshKey(prev => prev + 1);
  };

  /* ================= FETCH ORDERS ================= */

  const fetchOrders = async (pageNumber = 1) => {

    if (loading || !hasMore) return;

    setLoading(true);

    let url = `order/order-list/?page=${pageNumber}&`;

    if (customerFilter) url += `customer=${customerFilter}&`;
    if (dateFilter) url += `date=${dateFilter}&`;
    if (statusFilter) url += `status=${statusFilter}`;

    try {
      const res = await API.get(url);

      if (pageNumber === 1) {
        setOrders(res.data.results);
      } else {
        setOrders(prev => [...prev, ...res.data.results]);
      }

      setHasMore(res.data.has_next);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }

    setLoading(false);
  };

  /* ================= FILTER RESET ================= */

  useEffect(() => {
    setOrders([]);
    setHasMore(true);
    setPage(1);
    fetchOrders(1);
  }, [customerFilter, dateFilter, statusFilter]);

  /* ================= PAGE CHANGE ================= */

  useEffect(() => {
    if (page > 1) {
      fetchOrders(page);
    }
  }, [page]);

  useEffect(() => {
    const container = scrollRef.current;

    if (!container || loading || !hasMore) return;

    // If content is not scrollable
    if (container.scrollHeight <= container.clientHeight) {
      setPage(prev => prev + 1);
    }

  }, [orders]);

  /* ================= UPDATE STATUS ================= */

  const updateStatus = async (itemId, status) => {
    await API.patch(`order/update-order-item-status/${itemId}/`, {
      status: status
    });

    setOrders([]);
    setHasMore(true);
    setPage(1);
    fetchOrders(1);
  };

  /* ================= RENDER ================= */

  return (
    <div className="container-fluid h-100">

      <div className="row h-100">

        {/* ================= LEFT PANEL ================= */}

        <div className={showInventory ? "col-md-8 d-flex flex-column h-100" : "col-md-12 d-flex flex-column h-100"}>

          {/* HEADER */}
          <div className="p-3 border-bottom">

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Orders</h5>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowFilters(prev => !prev)}
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                  {activeFilterCount > 0 && (
                    <span className="badge bg-dark ms-2">{activeFilterCount}</span>
                  )}
                </button>

                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowInventory(!showInventory)}
                >
                  {showInventory ? "Hide Inventory" : "Show Inventory"}
                </button>

                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setShowModal(true)}
                >
                  New Order
                </button>
              </div>
            </div>

            {/* FILTER BAR */}
            {showFilters && (
              <div className="row g-2 bg-light border rounded p-2 mx-0">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Filter by customer..."
                    value={customerFilter}
                    onChange={e => setCustomerFilter(e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={dateFilter}
                    onChange={e => setDateFilter(e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <select
                    className="form-select form-select-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="DONE">Done</option>
                  </select>
                </div>

                <div className="col-md-2">
                  <button
                    className="btn btn-sm btn-outline-secondary w-100"
                    onClick={() => {
                      setCustomerFilter("");
                      setDateFilter("");
                      setStatusFilter("");
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ================= SCROLLABLE ORDER LIST ================= */}

          <div
            ref={scrollRef}
            style={{ flex: 1, overflowY: "auto" }}
            onScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.target;

              if (
                scrollHeight - scrollTop <= clientHeight + 50 &&
                hasMore &&
                !loading
              ) {
                setPage(prev => prev + 1);
              }
            }}
          >
            <div className="p-3">

              {orders.length === 0 && !loading && (
                <div className="text-center text-muted p-3">
                  No orders found
                </div>
              )}

              {orders.map((group, index) => {

                const today = new Date()
                  .toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric"
                  })
                  .replace(/ /g, "-");
                const isToday = group.order_date === today;

                return (
                  <div
                    key={index}
                    className="mb-3"
                    style={{
                      border: "1px solid #dee2e6",
                      backgroundColor: isToday ? "#e9f9ee" : "#fff"
                    }}
                  >

                    <div
                      className="px-3 py-2 d-flex justify-content-between align-items-center"
                      style={{
                        backgroundColor: isToday ? "#d1f2dc" : "#f8f9fa",
                        borderBottom: "1px solid #dee2e6"
                      }}
                    >
                      <div>
                        <strong>{group.customer_name}</strong>
                        <span className="text-muted ms-2 small">
                          {group.order_date}
                        </span>
                        {isToday && (
                          <span className="badge bg-success ms-2">
                            Today
                          </span>
                        )}
                      </div>

                      <span className="badge bg-dark">
                        Total Sheets: {group.items.reduce((total, item) => total + parseInt(item.quantity), 0)}
                      </span>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                      <table
                        className="table table-sm mb-0 align-middle"
                        style={{ tableLayout: "fixed", width: "100%" }}
                      >
                        <colgroup>
                          <col style={{ width: "36%" }} />
                          <col style={{ width: "10%" }} />
                          <col style={{ width: "34%" }} />
                          <col style={{ width: "20%" }} />
                        </colgroup>
                        <thead className="table-light">
                          <tr>
                            <th className="px-3 py-1 text-start">Sheet</th>
                            <th className="px-3 py-1 text-center">Qty</th>
                            <th className="px-3 py-1 text-start">Strips</th>
                            <th className="px-3 py-1 text-start">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.items.map((item, i) => (
                            <tr>
                              <td className="px-3 py-1">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <span>{item.sheet_name}</span>
                                  {item.is_replacement && (
                                    <span className="badge bg-warning text-dark">RE</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-1 text-center">{item.quantity}</td>
                              <td className="px-3 py-1">
                                {item.strips.map(s => (
                                  <span key={s} className="badge bg-light text-dark me-1">
                                    {s}
                                  </span>
                                ))}
                              </td>
                              <td
                                className={`px-3 py-1 ${item.status === "DONE" ? "bg-green-50" : ""
                                  }`}
                              >
                                <select
                                  className={`form-select form-select-sm ${item.status === "DONE"
                                      ? "text-green-700 border-green-400"
                                      : ""
                                    }`}
                                  value={item.status}
                                  onChange={(e) =>
                                    updateStatus(item.item_id, e.target.value)
                                  }
                                >
                                  <option value="PENDING">Pending</option>
                                  <option value="DONE">Done</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                  </div>
                );
              })}

              {loading && (
                <div className="text-center p-3">
                  <div className="spinner-border spinner-border-sm"></div>
                </div>
              )}

              {!hasMore && orders.length > 0 && (
                <div className="text-center text-muted p-3">
                  No more orders
                </div>
              )}

            </div>
          </div>

        </div>

        {/* ================= RIGHT PANEL ================= */}

        {showInventory && (
          <div className="col-md-4 d-flex flex-column h-100 border-start">
            <div style={{ flex: 1, overflowY: "auto" }}>
              <div className="p-3">
                <InventorySidebar key={inventoryRefreshKey} />
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ================= MODAL ================= */}

      {showModal && (
        <CreateOrderModal
          onClose={() => setShowModal(false)}
          refreshOrders={() => {
            setOrders([]);
            setHasMore(true);
            setPage(1);
            fetchOrders(1);
          }}
          refreshInventory={refreshInventory}
        />
      )}

    </div>
  );
}

export default Orders;